// Finance Books — Dashboard section. Live KPIs + recent docs.
import React, { useEffect, useState } from 'react';
import fb from '../api';
import { PageHeader, Kpi, Pill, fmtMoney, fmtDate, SOFT, PANEL, BSC_ORANGE, statusTone } from '../ui';

export default function Dashboard({ onNav }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await fb.post('/seed');
        const d = await fb.get('/dashboard');
        setData(d);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-white/40 text-sm">Loading dashboard…</div>;
  if (!data) return <div className="text-white/40 text-sm">No data.</div>;

  return (
    <div data-testid="fb-dashboard">
      <PageHeader
        title="Dashboard"
        subtitle="Live financial overview · double-entry accounting"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total Receivable" value={fmtMoney(data.total_receivable)} sub={`${data.open_invoices} open · ${data.overdue_invoices} overdue`} tone="orange" testid="kpi-ar" />
        <Kpi label="Total Payable" value={fmtMoney(data.total_payable)} sub={`${data.open_bills} open bills`} tone="red" testid="kpi-ap" />
        <Kpi label="Bank Balance" value={fmtMoney(data.bank_balance)} sub={`${data.banks?.length || 0} accounts`} tone="green" testid="kpi-bank" />
        <Kpi label="Profit (MTD)" value={fmtMoney(data.profit_mtd)} sub={`Rev ${fmtMoney(data.revenue_mtd)} · Exp ${fmtMoney(data.expense_mtd)}`} tone={data.profit_mtd >= 0 ? 'green' : 'red'} testid="kpi-profit-mtd" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Kpi label="Revenue YTD" value={fmtMoney(data.revenue_ytd)} tone="blue" testid="kpi-rev-ytd" />
        <Kpi label="Expense YTD" value={fmtMoney(data.expense_ytd)} tone="orange" testid="kpi-exp-ytd" />
        <Kpi label="Profit YTD" value={fmtMoney(data.profit_ytd)} tone={data.profit_ytd >= 0 ? 'green' : 'red'} testid="kpi-profit-ytd" />
        <Kpi label="Low Stock Items" value={data.low_stock_items} sub={`${data.items} items · ${data.customers} customers · ${data.vendors} vendors`} tone={data.low_stock_items > 0 ? 'orange' : 'neutral'} testid="kpi-low-stock" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className={`${PANEL} rounded-xl p-5`} data-testid="fb-recent-invoices">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium">Recent Invoices</h3>
            <button className="text-xs text-white/40 hover:text-white" onClick={() => onNav?.('invoices')}>View all →</button>
          </div>
          {(!data.recent_invoices || data.recent_invoices.length === 0) ? (
            <p className="text-white/40 text-sm italic">No invoices yet.</p>
          ) : data.recent_invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between py-2 border-t border-white/5">
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{inv.doc_no} · {inv.customer_name}</div>
                <div className="text-xs text-white/40">{fmtDate(inv.date)}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-white/90 tabular-nums">{fmtMoney(inv.grand_total)}</span>
                <Pill tone={statusTone(inv.status)}>{inv.status}</Pill>
              </div>
            </div>
          ))}
        </div>

        <div className={`${PANEL} rounded-xl p-5`} data-testid="fb-recent-bills">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium">Recent Bills</h3>
            <button className="text-xs text-white/40 hover:text-white" onClick={() => onNav?.('bills')}>View all →</button>
          </div>
          {(!data.recent_bills || data.recent_bills.length === 0) ? (
            <p className="text-white/40 text-sm italic">No bills yet.</p>
          ) : data.recent_bills.map((b) => (
            <div key={b.id} className="flex items-center justify-between py-2 border-t border-white/5">
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{b.doc_no} · {b.vendor_name}</div>
                <div className="text-xs text-white/40">{fmtDate(b.date)}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-white/90 tabular-nums">{fmtMoney(b.grand_total)}</span>
                <Pill tone={statusTone(b.status)}>{b.status}</Pill>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={`${PANEL} rounded-xl p-5 mt-4`} data-testid="fb-bank-accounts">
        <h3 className="text-white font-medium mb-3">Bank & Cash Accounts</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data.banks || []).map((b) => (
            <div key={b.id} className={`${SOFT} rounded-lg p-4 flex items-center justify-between`}>
              <div className="text-sm text-white/85">{b.name}</div>
              <div className="text-sm tabular-nums text-white font-medium" style={{ color: b.balance >= 0 ? '' : '#fb7185' }}>{fmtMoney(b.balance)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
